import React, { useState, useEffect, useRef } from 'react';
import {
    Terminal,
    Play,
    Pause,
    RotateCcw,
    Filter,
    AlertCircle,
    Info,
    AlertTriangle,
    FileText
} from 'lucide-react';
import apiService from '../services/api';
import { formatDateWithDefaultTimezone } from '../utils/timezone';

const LogManagements = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [filters, setFilters] = useState({
        level: '',
        username: '',
        dateFrom: '',
        dateTo: ''
    });
    const [users, setUsers] = useState([]);
    const [hasMoreLogs, setHasMoreLogs] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [defaultTimezone, setDefaultTimezone] = useState('UTC');
    const logsEndRef = useRef(null);
    const refreshIntervalRef = useRef(null);
    const observerRef = useRef(null);
    const latestIdRef = useRef('000000000000000000000000');
    const olderIdRef = useRef('ffffffffffffffffffffffff');

    useEffect(() => {
        fetchUsers();
        fetchConfig();
        fetchData();
    }, [filters]);

    useEffect(() => {
        if (logs.length > 0) {
            latestIdRef.current = logs[0]._id;
            olderIdRef.current = logs[logs.length - 1]._id;
        } else {
            latestIdRef.current = '000000000000000000000000';
            olderIdRef.current = 'ffffffffffffffffffffffff';
        }
    }, [logs]);

    useEffect(() => {
        if (autoRefresh) {
            refreshIntervalRef.current = setInterval(() => {
                fetchNewLogs();
            }, 2000); // Refresh every 2 seconds
        } else {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
        }

        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
        };
    }, [autoRefresh, filters]);

    // Intersection Observer for lazy loading
    useEffect(() => {
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMoreLogs && !loadingMore) {
                    loadMoreLogs();
                }
            },
            { threshold: 0.1 }
        );

        if (logsEndRef.current) {
            observerRef.current.observe(logsEndRef.current);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [hasMoreLogs, loadingMore]);

    const fetchData = async () => {
        try {
            setError('');
            setLoading(true);
            setLogs([]);
            latestIdRef.current = '000000000000000000000000';
            olderIdRef.current = 'ffffffffffffffffffffffff';

            await loadMoreLogs();

        } catch (err) {
            console.error('Error fetching logs:', err);
            setError('Failed to fetch logs');
        } finally {
            setLoading(false);
        }
    };

    const loadMoreLogs = async () => {
        if (loadingMore || !hasMoreLogs) return;

        try {
            setLoadingMore(true);
            const response = await apiService.getOldLogs(olderIdRef.current, filters, 50);

            if (response.data && response.data.length > 0) {
                setLogs(prev => [...prev, ...response.data]);
                setHasMoreLogs(response.data.length === 50);
            } else {
                setHasMoreLogs(false);
            }
        } catch (err) {
            console.error('Error loading more logs:', err);
        } finally {
            setLoadingMore(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const usersData = await apiService.getUsers();
            setUsers(usersData || []);
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    };

    const fetchConfig = async () => {
        try {
            const configData = await apiService.getConfig();
            const defaultConfig = configData.find(config => config.username === null);
            if (defaultConfig && defaultConfig.timeZone) {
                setDefaultTimezone(defaultConfig.timeZone);
            }
        } catch (err) {
            console.error('Error fetching config:', err);
        }
    };

    const fetchNewLogs = async () => {
        try {
            if (!latestIdRef.current) return;

            const response = await apiService.getNewLogs(latestIdRef.current, filters);

            if (response.data && response.data.length > 0) {
                setLogs(prev => [...response.data, ...prev]);
                latestIdRef.current = response.data[0]._id;
            }
        } catch (err) {
            console.error('Error fetching new logs:', err);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setHasMoreLogs(true);
    };

    const clearFilters = () => {
        setFilters({
            level: '',
            username: '',
            dateFrom: '',
            dateTo: ''
        });
        setHasMoreLogs(true);
    };

    const getLogIcon = (level) => {
        switch (level) {
            case 'error':
                return <AlertCircle className="w-4 h-4 text-red-500" />;
            case 'warn':
                return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
            case 'info':
                return <Info className="w-4 h-4 text-blue-500" />;
            case 'log':
                return <FileText className="w-4 h-4 text-gray-500" />;
            default:
                return <FileText className="w-4 h-4 text-gray-500" />;
        }
    };

    const getLogColor = (level) => {
        switch (level) {
            case 'error':
                return 'text-red-400';
            case 'warn':
                return 'text-yellow-400';
            case 'info':
                return 'text-blue-400';
            case 'log':
                return 'text-gray-400';
            default:
                return 'text-gray-400';
        }
    };

    const formatDate = (date) => {
        return formatDateWithDefaultTimezone(date, defaultTimezone);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                    <Terminal className="w-8 h-8 mr-3 text-blue-600" />
                    Logs
                </h1>
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => setAutoRefresh(!autoRefresh)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${autoRefresh
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : 'bg-green-600 text-white hover:bg-green-700'
                            }`}
                    >
                        {autoRefresh ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        {autoRefresh ? 'Pause' : 'Live'}
                    </button>
                    <button
                        onClick={fetchNewLogs}
                        disabled={autoRefresh}
                        className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors bg-blue-600 ${autoRefresh ? 'opacity-80' : 'hover:bg-blue-700'}`}
                    >
                        <RotateCcw className="w-4 h-4" />
                        Refresh
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-md p-4">
                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
                        <select
                            value={filters.level}
                            onChange={(e) => handleFilterChange('level', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">All Levels</option>
                            <option value="error">Error</option>
                            <option value="warn">Warning</option>
                            <option value="info">Info</option>
                            <option value="log">Log</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
                        <select
                            value={filters.username}
                            onChange={(e) => handleFilterChange('username', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">All Users</option>
                            <option value="system">system</option>
                            {users.map(user => (
                                <option key={user.username} value={user.username}>
                                    {user.username}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="datetime-local"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="datetime-local"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div> */}

                    <div className="flex items-end">
                        <button
                            onClick={clearFilters}
                            className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Logs Console */}
            <div className="bg-black rounded-lg shadow-md overflow-hidden">

                <div className="h-96 overflow-y-auto p-4 font-mono text-sm">
                    {logs.length === 0 ? (
                        <div className="text-gray-500 text-center py-8">
                            No logs found with current filters
                        </div>
                    ) : (
                        logs.map((log) => (
                            <div
                                key={log._id}
                                className="mb-2 border-l-2 border-gray-700 pl-3 hover:bg-gray-900 rounded"
                            >
                                <div className="flex items-start gap-2 flex-0">
                                    <span className={`${getLogColor(log.level)}`}>
                                        {getLogIcon(log.level)}
                                    </span>
                                    <span className="text-gray-400 whitespace-nowrap">
                                        {formatDate(log.createdAt)}
                                    </span>
                                    <span className="text-blue-400">[{log.level.toUpperCase()}]</span>
                                    <span className={log.username === 'system' ? "text-gray-500" : "text-green-400"}>[{log.username}]</span>
                                    <span className="ml-4 text-gray-300 whitespace-pre-wrap">
                                        {log.message}
                                    </span>
                                </div>
                            </div>
                        ))
                    )}
                    {hasMoreLogs && (
                        <div ref={logsEndRef} className="text-gray-500 text-center py-4">
                            Loading more logs...
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
};

export default LogManagements;
